<template>
  <div class="flex h-full flex-col">
    <!-- Banner -->
    <BannerComponent
      v-if="notificationStore.banner"
      v-bind="notificationStore.banner"
    />
    <div id="main-layout" class="h-full overflow-hidden md:flex">
      <!-- Menu -->
      <MenuComponent ref="menuRef" :items="menuItems">
        <template #Portfolio>
          <PortfolioMenuItemComponent ref="portfolioMenuItemRef" />
        </template>
      </MenuComponent>
      <!-- Content -->
      <div class="flex h-full w-full flex-col overflow-hidden">
        <MenuHeaderComponent class="md:hidden" @open-menu="menuRef?.openMenu" />
        <StatusBarComponent
          v-if="!hideStatusbar && sessionStore.isAuthenticated"
          class="flex-0"
        />
        <main
          ref="scrollContainerRef"
          class="flex flex-1 flex-col overflow-x-hidden overflow-y-auto px-4 py-6 md:px-10"
        >
          <!-- Errors container -->
          <TransitionGroup
            name="notification"
            tag="ul"
            class="sticky top-0 z-10 grid gap-4 min-[2048px]:grid-cols-3 xl:grid-cols-2"
            :class="notificationStore.errors.length ? 'mb-6' : ''"
          >
            <li
              v-for="notification in notificationStore.errors"
              :key="notification.uid"
            >
              <ErrorComponent v-bind="notification" />
            </li>
          </TransitionGroup>
          <!-- Current view -->
          <LoadingWrapper :loading="loadingRouterView">
            <RouterView v-if="showRouterView" />
          </LoadingWrapper>
          <!-- Footer -->
          <footer
            class="mt-6 flex h-full w-full grow flex-col items-center justify-between text-center text-sm text-gray-300"
          >
            <div class="bg-finance-200 grow" />
            <p>
              Versie <span class="italic">{{ version }}</span>
            </p>
            <p>© {{ getYear(startOfToday()) }} Repowered</p>
          </footer>
        </main>
      </div>
    </div>
    <!-- Toasts container -->
    <div class="absolute right-4 bottom-4 w-90 space-y-4">
      <TransitionGroup name="notification">
        <ToastComponent
          v-for="notification in notificationStore.toasts"
          :key="notification.uid"
          v-bind="notification"
        />
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount } from "vue"
import { RouterView, useRoute, useRouter } from "vue-router"
import { startOfToday, getYear } from "date-fns"
import useNotificationStore from "@/stores/notificationStore.js"
import useSessionStore from "@/stores/sessionStore.js"
import useNavigationStore from "@/stores/navigationStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import useFilterStore from "@/stores/filterStore.js"
import useNotificationsListStore from "@/stores/repower/notificationsListStore.js"
import { useNotificationsPolling } from "@/composables/useNotificationsPolling.js"
import { LoadingWrapper } from "@repowerednl/ui-component-library"
import BannerComponent from "@/components/notifications/BannerComponent.vue"
import ErrorComponent from "@/components/notifications/ErrorComponent.vue"
import ToastComponent from "@/components/notifications/ToastComponent.vue"
import MenuHeaderComponent from "@/components/navigation/MenuHeaderComponent.vue"
import MenuComponent from "@/components/navigation/MenuComponent.vue"
import PortfolioMenuItemComponent from "@/components/navigation/PortfolioMenuItemComponent.vue"
import StatusBarComponent from "@/components/navigation/StatusBarComponent.vue"

const notificationStore = useNotificationStore()
const sessionStore = useSessionStore()
const navigationStore = useNavigationStore()
const meteringPointsStore = useMeteringPointsStore()
const filterStore = useFilterStore()
const notificationsListStore = useNotificationsListStore()
const route = useRoute()
const router = useRouter()

useNotificationsPolling(notificationsListStore, {
  enabled: () => sessionStore.isAuthenticated,
})

const menuRef = ref()
const scrollContainerRef = ref()
const portfolioMenuItemRef = ref()
// eslint-disable-next-line no-undef
const version = __APP_VERSION__
const showRouterView = ref(false)

const menuItems = computed(() => {
  if (!sessionStore.isAuthenticated) {
    return [
      {
        name: "Inloggen",
        icon: "user",
        section: "Account",
        route: "login",
      },
      {
        name: "Contact",
        icon: "customer-service-2",
        section: "Help",
        route: "contact",
      },
    ]
  }
  return [
    {
      name: "Portfolio",
      icon: "briefcase-4",
      section: "Portfolio",
      action: () => portfolioMenuItemRef.value?.openMenu(),
      ping: !filterStore.currentPortfolio,
    },
    {
      name: "Dashboard",
      icon: "dashboard-3",
      section: "Overzicht",
      route: "dashboard",
    },
    {
      name: "Installaties",
      icon: "flashlight",
      section: "Energiesysteem",
      route: "assets",
      activeRoutes: ["assets-information", "assets-analysis"],
    },
    {
      name: "Inzichten",
      icon: "file-chart",
      section: "Energiesysteem",
      route: "insights",
    },
    {
      name: "Energierekening",
      icon: "money-euro-circle",
      section: "Energiesysteem",
      route: "energy-bill",
    },
    ...(filterStore.currentPortfolio?.energyHubAPs.length > 0
      ? [
          {
            name: "Energy Hub",
            icon: "network",
            section: "Energiesysteem",
            route: "energy-hub",
          },
        ]
      : []),
    {
      name: "Energiemarkt",
      icon: "stock",
      section: "Energiesysteem",
      route: "markets",
    },
    {
      name: "Berichten",
      icon: "chat-1",
      section: "Mijn",
      route: "messages",
      ping: notificationsListStore.hasUnseen,
      activeRoutes: ["messages-overview", "messages-notifications"],
    },
    {
      name: "Documenten",
      icon: "folder-6",
      section: "Mijn",
      route: "documents",
      activeRoutes: ["documents-invoices", "documents-exports"],
    },
    {
      name: "Instellingen",
      icon: "settings-3",
      section: "Mijn",
      route: "settings",
      activeRoutes: ["settings-account", "settings-billing"],
    },
    {
      name: "Contact",
      icon: "customer-service-2",
      section: "Help",
      route: "messages-contact",
      activeRoutes: [
        "messages-contact",
        "messages-contact-general",
        "messages-contact-disruption",
      ],
    },
  ]
})

/**
 * Keep the router view in a loading state as long as
 * - the session is still being fetched
 * - the metering point query is still being applied. This prevents the
 *   displayed information from being visibly replaced by the query settings.
 */
const loadingRouterView = computed(() => {
  return (
    sessionStore.loading ||
    (sessionStore.isAuthenticated &&
      navigationStore.isApplyingMeteringPointQuery)
  )
})

const hideStatusbar = computed(() => {
  return ["login", "logout", "energy-bill"].includes(route.name)
})

function onLoadMeteringPointsFail(error) {
  useNotificationStore().pushError(
    "Fout bij het laden van installaties",
    `Installaties of bovenliggende allocatiepunten en portfolios konden niet
    worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-metering-points-fail",
  )
}

/**
 * 'showRouterView' is only set to true after the router change is awaited. This
 * prevents possible API calls being made before the route change is completed.
 */
async function onSessionAuthenticated() {
  meteringPointsStore.loadMeteringPoints(onLoadMeteringPointsFail)
  navigationStore.applyQuery()
  if (route.meta.onlyPublic) {
    await router.push({ name: route.meta.authRedirect ?? "dashboard" })
  }
  showRouterView.value = true
  notificationStore.pushToast("Je bent ingelogd.")
}

/**
 * 'showRouterView' is only set to true after the router change is awaited. This
 * prevents possible API calls being made before the route change is completed.
 */
async function onSessionUnauthenticated() {
  if (!route.meta.public) {
    await router.push({ name: "login" })
  }
  showRouterView.value = true
}

function onSessionFail(error) {
  onSessionUnauthenticated()
  notificationStore.pushError(
    "Fout bij het ophalen van sessie",
    `Sessie kon niet worden opgehaald. Probeer in te loggen of probeer het later opnieuw. (code: ${error.code})`,
  )
}

/**
 * Reset container scroll position when the route changes
 */
watch(
  () => route.name,
  () => {
    scrollContainerRef.value.scrollTo(0, 0)
  },
)

onBeforeMount(() => {
  sessionStore.requestCsrfToken()
  sessionStore.getSession(
    onSessionAuthenticated,
    onSessionUnauthenticated,
    onSessionFail,
  )
})
</script>

<style>
/*
  The DatePicker modal is teleported to this component, which requires the
  following CSS variables to be set here specifically.
*/
* {
  --dp-text-color: var(--color-gray-900);
  --dp-menu-border-color: var(--color-gray-200);
  --dp-border-color: var(--color-gray-300);
  --dp-primary-color: var(--color-primary-500);
  --dp-secondary-color: var(--color-gray-300);
  --dp-icon-color: var(--color-gray-900);
  --dp-hover-color: var(--color-primary-50);
  --dp-hover-text-color: var(--color-primary-500);
  --dp-range-between-dates-background-color: var(--color-primary-50);
  --dp-range-between-dates-text-color: var(--color-primary-500);
  --dp-range-between-border-color: none;
}
</style>

<style>
.notification-move {
  transition: all 0.3s ease;
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/*
  Ensure leaving items are taken out of layout flow so that moving animations
  can be calculated correctly.
*/
.notification-leave-active {
  position: absolute;
}
</style>
